import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, ChevronDown, Image as ImageIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import ServiceGallery from '@/components/ServiceGallery';

// Helper per ottenere icona dinamica
const getIcon = (iconName) => {
  if (!iconName) return null;
  return LucideIcons[iconName] || null;
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, settingsRes] = await Promise.all([
          api.get('/public/services'),
          api.get('/public/settings')
        ]);
        setServices(servicesRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Helper per ottenere testi CMS con fallback
  const t = (key, fallback) => settings?.texts?.[key] || fallback;

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="services-page">
      {/* Header */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground">
            {t('services_page_label', 'Servizi')}
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl mt-4 mb-6">
            {t('services_page_title', 'Cosa Posso Fare per Te')}
          </h1>
          <p className="font-accent text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('services_page_subtitle', 'Ogni progetto è unico e merita un approccio personalizzato. Scopri i miei servizi.')}
          </p>
        </motion.div>
      </div>

      {/* Services Grid */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="border border-border p-8 lg:p-12 hover:border-foreground transition-colors duration-500"
              data-testid={`service-item-${index}`}
            >
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <ServiceGallery service={service} />
                  {/* Icon */}
                  {service.icon && (() => {
                    const Icon = getIcon(service.icon);
                    return Icon ? (
                      <div className="w-12 h-12 border border-border rounded-sm flex items-center justify-center mb-6">
                        <Icon size={24} />
                      </div>
                    ) : null;
                  })()}
                  
                  <h2 className="font-heading text-3xl lg:text-4xl mb-4">{service.title}</h2>
                  <p className="text-muted-foreground mb-6">{service.description}</p>

                  {/* Meta Info */}
                  <div className="flex flex-wrap gap-6 mb-8 text-sm">
                    {service.duration && (
                      <div>
                        <span className="text-muted-foreground">Durata:</span>
                        <span className="ml-2 font-medium">{service.duration}</span>
                      </div>
                    )}
                    {service.starting_price && (
                      <div>
                        <span className="text-muted-foreground">A partire da:</span>
                        <span className="ml-2 font-medium">{service.starting_price}</span>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  {service.features?.length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-sm font-medium tracking-widest uppercase text-muted-foreground mb-4">
                        Cosa Include
                      </h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-3">
                            <Check size={16} className="text-foreground flex-shrink-0" />
                            <span className="text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* FAQ */}
                  {service.faq?.length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-sm font-medium tracking-widest uppercase text-muted-foreground mb-4">
                        FAQ
                      </h4>
                      <Accordion type="single" collapsible className="w-full">
                        {service.faq.map((item, i) => (
                          <AccordionItem key={i} value={`faq-${i}`} className="border-border">
                            <AccordionTrigger className="text-left hover:no-underline py-4">
                              {item.question}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground">
                              {item.answer}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  )}
                </div>

                <div className="pt-6 border-t border-border">
                  <Link to="/prenota">
                    <Button className="w-full sm:w-auto" data-testid={`service-cta-${index}`}>
                      {t('btn_request_quote', 'Richiedi Preventivo')}
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mt-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-muted/50 p-12 lg:p-16 text-center"
        >
          <h2 className="font-heading text-3xl sm:text-4xl mb-4">
            {t('services_cta_title', 'Non trovi quello che cerchi?')}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            {t('services_cta_subtitle', 'Ogni progetto è diverso. Contattami per discutere le tue esigenze specifiche e ricevere un preventivo personalizzato.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prenota">
              <Button size="lg" data-testid="services-cta-book">
                {t('btn_book_consultation', 'Prenota una Consulenza')}
              </Button>
            </Link>
            <Link to="/contatti">
              <Button size="lg" variant="outline" data-testid="services-cta-contact">
                {t('btn_contact', 'Contattami')}
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
